/**
 * De rondleidingen achter de wegwijzer van Slim zoeken.
 *
 * <p>De api kent de taken (welke vraag hoort bij welk scherm) en levert geschreven stappen. Dit
 * bestand voegt daar het aanwijzen aan toe: per stap wélk element op het scherm de spot krijgt.
 *
 * <p>Waarom dat hier staat en niet in de api: een CSS-selector is kennis van de schermen, en die
 * hoort niet in een Java-catalogus. De api geeft alleen een taak-id terug; hier ligt wat je dan
 * ziet. Bestaat er voor een taak geen rondleiding, dan blijft de geschreven lijst staan — dat is
 * de terugval, geen fout.
 *
 * <p><b>Elke stap hieronder is tegen de draaiende app gehouden, niet opgeschreven uit het hoofd.</b>
 * Dat was nodig ook: de eerste versie stuurde je voor het inboeken naar een menu-item "Inboeken" in
 * de zijbalk, en dat bestaat niet — die knop staat op het dashboard. En "Bewerken" op het
 * tariefscherm werd nooit gevonden omdat het een icoonknop zonder tekst is. Een stap die naar een
 * knop wijst die er niet is, is erger dan geen stap: je gaat zoeken naar iets wat nergens staat.
 *
 * <p>Wie hier een rondleiding bijzet of een scherm verbouwt: loop de stappen na in de app. Dat is
 * met een handjevol regels in de console te doen — per stap naar de route, element zoeken op
 * selector plus naam, aanklikken, volgende stap. Achttien stappen kosten zo een paar minuten.
 *
 * <p>Elementen worden gezocht op klasse plus naam, waarbij "naam" de zichtbare tekst is óf het
 * aria-label óf de title. De backoffice heeft (nog) geen
 * `data-`haakjes voor dit doel, en die alsnog overal bijzetten zou een grote wijziging zijn in
 * schermen die verder niets met de rondleiding te maken hebben. De tekst is Nederlands, net als de
 * rondleiding zelf; wie later een haakje toevoegt kan de selector zonder gevolgen vervangen.
 */
export interface RondleidingStap {
  /** Ga eerst naar dit scherm. Weglaten betekent: blijf waar je bent. */
  route?: string;
  /** Waar de spot op valt. */
  selector: string;
  /** Welke van de treffers, als de selector er meer geeft — op zichtbare tekst. */
  bevat?: string;
  titel: string;
  tekst: string;
  /**
   * Deze stap wijst iets aan dat pas bestaat nadat je zelf iets hebt gedaan (een rij openen,
   * een dialoog openen). Wordt hij niet gevonden, dan is dat geen fout maar het einde van wat de
   * rondleiding kan tonen: hij zegt dat eerlijk in plaats van naar niets te wijzen.
   */
  optioneel?: boolean;
  /**
   * Bestaat dit element niet, sla de stap dan over in plaats van hem te tonen. Voor stappen die
   * van het toestel afhangen: de menuknop bestaat alleen op een smal scherm, want daar is de
   * zijbalk een lade. Op een breed scherm staat het menu er gewoon en is de stap zinloos.
   */
  overslaanAlsAfwezig?: boolean;
}

/** De menuknop op smalle schermen; op een breed scherm bestaat hij niet en valt de stap weg. */
const OPEN_HET_MENU: RondleidingStap = {
  selector: ".fui-shell__menu-knop, [aria-label='Navigatie openen']",
  titel: "Klik op het menu",
  tekst: "Op een smal scherm zit de navigatie achter deze knop. Tik erop om verder te gaan.",
  overslaanAlsAfwezig: true,
};

/**
 * De twee stappen van een gewoon menupad: eerst de groep openklappen, dan het item aanklikken.
 *
 * De namen komen niet uit het hoofd maar uit `App.tsx` zelf — daar staat welke items in welke
 * groep zitten en welk Nederlands label ze dragen. Een rondleiding die naar "Producten" wijst
 * terwijl het menu "Producten & Diensten" zegt, laat je zoeken naar iets wat er niet staat.
 */
function viaHetMenu(groep: string, item: string, waarom: string): RondleidingStap[] {
  return [
    OPEN_HET_MENU,
    {
      selector: ".fui-nav-section__summary",
      bevat: groep,
      titel: `Klik op ${groep}`,
      tekst: `${item} staat in deze groep.`,
    },
    {
      selector: ".fui-nav-item",
      bevat: item,
      titel: `Klik op ${item}`,
      tekst: waarom,
    },
  ];
}

export const RONDLEIDINGEN: Record<string, RondleidingStap[]> = {
  "asiel-tarief": [
    ...viaHetMenu(
      "Producten",
      "Asiel tarieven",
      "Hier staan de regels die bepalen wat er bij afstand of adoptie wordt gerekend."),
    {
      route: "/asiel-tarief",
      selector: ".fui-table tbody tr",
      titel: "Kies de regel die je wilt wijzigen",
      tekst:
        "Elke regel is één tarief, met zijn voorwaarden en het bedrag. Zoek de regel die je wilt "
        + "wijzigen — bijvoorbeeld “Afstand hond pup”.",
      optioneel: true,
    },
    {
      route: "/asiel-tarief",
      // De potloodknop in de kolom Acties; hij heeft geen tekst, alleen een aria-label.
      selector: ".tarief-rijactie",
      bevat: "Bewerken",
      titel: "Klik op Bewerken",
      tekst:
        "Hier pas je de voorwaarden aan (attribuut, operator, waarde) en het bedrag bij Resultaat. "
        + "Let op de spanwijdte: “leeftijd < 120” overlapt met “leeftijd < 6”, en dan wint "
        + "de scherpste regel.",
      optioneel: true,
    },
    {
      route: "/asiel-tarief",
      selector: "button",
      bevat: "Test tarief",
      titel: "Klik op Test tarief",
      tekst:
        "Vul hier een dier in en kijk welk tarief eruit komt. Zo zie je meteen of je regel doet wat "
        + "je bedoelt, vóór er iemand aan de balie staat.",
      optioneel: true,
    },
  ],

  "dier-inboeken": [
    OPEN_HET_MENU,
    {
      selector: ".fui-nav-item",
      bevat: "Dashboard",
      titel: "Klik op Dashboard",
      tekst: "Inboeken staat niet in de zijbalk maar op het dashboard, bij de knoppen bovenaan.",
    },
    {
      route: "/dashboard-react",
      selector: ".fcc-hero-actions .fcc-btn",
      bevat: "Inboeken",
      titel: "Klik op Inboeken",
      tekst: "Deze knop opent de wizard waarmee je een dier registreert dat binnenkomt.",
    },
    // De wizard opent op de vraag naar het aantal, niet op de herkomst. Dat stond hier eerst wél
    // zo, en dan zegt de kaart "Hoe komt het dier binnen?" terwijl er "Hoeveel dieren wil je
    // inboeken?" op het scherm staat. De volgorde komt uit InboekenWizard.tsx zelf: count →
    // animals → type, en pas daarna splitst hij per herkomst.
    {
      selector: ".fawkes-om-modal__body",
      titel: "Begin bij het aantal",
      tekst:
        "Eén dier, of meerdere tegelijk uit hetzelfde nest of hetzelfde huishouden. Bij meerdere "
        + "vul je erbij hoeveel; de stappen daarna gelden dan voor allemaal.",
      optioneel: true,
    },
    {
      selector: ".fawkes-om-modal__steps",
      titel: "De stappen erna",
      tekst:
        "Eerst de diergegevens, dan de vraag hoe het dier binnenkwam — afstandsdier, zwerver, "
        + "retour na plaatsing of uitwisseling. Díé keuze bepaalt de rest en welke verklaring er "
        + "wordt afgedrukt. Er wordt pas iets vastgelegd als je aan het eind bevestigt.",
      optioneel: true,
    },
    {
      selector: ".fawkes-om-modal__footer",
      titel: "Verder en terug",
      tekst: "Onderin staan Annuleren, Terug en de knop die je een stap verder brengt.",
      optioneel: true,
    },
  ],

  "relatie-toevoegen": [
    OPEN_HET_MENU,
    {
      selector: ".fui-nav-item",
      bevat: "Relaties",
      titel: "Klik op Relaties",
      tekst: "Klanten, eigenaren en vinders staan hier bij elkaar.",
    },
    {
      route: "/relaties",
      selector: "button",
      bevat: "Nieuwe relatie",
      titel: "Klik op Nieuwe relatie",
      tekst: "Hiermee open je het formulier voor naam en adresgegevens.",
      optioneel: true,
    },
  ],

  "hok-toewijzen": [
    OPEN_HET_MENU,
    {
      selector: ".fui-nav-item",
      bevat: "Planning",
      titel: "Klik op Planning",
      tekst: "De plattegrond met alle hokken en wie waar zit.",
    },
    {
      route: "/planning-react",
      selector: ".fawkes-kennelplanning__canvas",
      titel: "De plattegrond",
      tekst:
        "Met een muis sleep je een dier uit de strook naar een hok. Op een telefoon of tablet "
        + "werkt tikken: eerst het dier oppakken, dan het hok aanwijzen.",
      optioneel: true,
    },
  ],

  "pension-tarief": viaHetMenu(
    "Producten",
    "Pension tarieven",
    "De dagprijzen van het pension, per diersoort en verblijfsvorm."),

  "crematie-tarief": viaHetMenu(
    "Producten",
    "Crematorium tarieven",
    "De tarieven per crematievorm en gewichtsklasse."),

  producten: viaHetMenu(
    "Producten",
    "Producten & Diensten",
    "Alles wat je op een factuur kunt zetten staat hier."),

  kasboek: viaHetMenu(
    "Financieel",
    "Kas uitgaven",
    "Contante ontvangsten en uitgaven leg je hier vast."),

  dagafsluiting: viaHetMenu(
    "Financieel",
    "Dagafsluiting",
    "Hier tel je de kas en sluit je de dag af."),

  gebruiker: viaHetMenu(
    "Security",
    "Gebruikers",
    "Medewerkers, hun rollen en bij welke vestiging ze horen."),

  /**
   * Taken die vanzelf ontstaan. Deze zit niet in de zijbalk maar achter Instellingen, dus hij deelt
   * het menupad niet — de selectoren komen uit ProfileCard (de knop linksonder) en SettingsTileCard
   * (die zet de tegelsleutel in de klassenaam, vandaar `settings-tile-card-taken`).
   */
  taakregel: [
    {
      route: "/dashboard-react",
      selector: ".fui-profile__action--settings",
      titel: "Klik op Instellingen",
      tekst: "Linksonder, onder je naam. De taakregels staan bij de instellingen en niet in de "
        + "zijbalk.",
    },
    {
      route: "/instellingen",
      selector: ".settings-tile-card-taken",
      titel: "Open de tegel Taken",
      tekst: "Automatische taakregels, deadline-regels en takenpakketten.",
      optioneel: true,
    },
    {
      selector: ".settings-tile-card-taken button, .settings-tile-card-taken a",
      bevat: "Openen",
      titel: "Klik op Openen",
      tekst:
        "Een sjabloon bepaalt wát er wordt aangemaakt en waardoor — de trigger, bijvoorbeeld het "
        + "inboeken van een dier. De regel eronder verfijnt dat per geval: op een veld en een "
        + "waarde, zoals de herkomst 'zwerver', met de termijn en wie hem krijgt.",
      optioneel: true,
    },
  ],

  sjabloon: viaHetMenu(
    "Communicatie",
    "Communicatie",
    "De brieven, e-mails en verklaringen die het systeem afdrukt."),
};

/** Is er voor deze taak een rondleiding, of blijft het bij de geschreven stappen? */
export function heeftRondleiding(taakId: string | null | undefined): boolean {
  return !!taakId && Array.isArray(RONDLEIDINGEN[taakId]) && RONDLEIDINGEN[taakId].length > 0;
}
